import { getCurrentUser, signOut as amplifySignOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type Me } from "./api";

/**
 * Where the visitor stands: not logged in; logged in to Cognito but not in a company yet
 * (needs sign-up or an invite); or a member with their user and company.
 */
export type Session =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "noAccount" }
  | { status: "member"; me: Me }
  | { status: "error"; message: string };

interface SessionValue {
  session: Session;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>({ status: "loading" });

  const refresh = useCallback(async () => {
    try {
      await getCurrentUser();
    } catch {
      setSession({ status: "signedOut" });
      return;
    }
    try {
      let me;
      try {
        me = await api.me();
      } catch (err) {
        // A dropped connection (TypeError: Failed to fetch) is often momentary: retry once.
        if (err instanceof ApiError) throw err;
        await new Promise((r) => setTimeout(r, 1000));
        me = await api.me();
      }
      setSession({ status: "member", me });
    } catch (err) {
      if (err instanceof ApiError && err.code === "no_account") setSession({ status: "noAccount" });
      else if (err instanceof ApiError && err.status === 401) setSession({ status: "signedOut" });
      else setSession({ status: "error", message: err instanceof ApiError ? err.message : "We couldn't reach the server. Check your connection." });
    }
  }, []);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setSession({ status: "signedOut" });
  }, []);

  useEffect(() => {
    void refresh();
    // Keep in step with sign-in/out in other tabs and token refresh failures.
    return Hub.listen("auth", ({ payload }) => {
      if (["signedIn", "signedOut", "tokenRefresh_failure"].includes(payload.event)) void refresh();
    });
  }, [refresh]);

  const value = useMemo(() => ({ session, refresh, signOut }), [session, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}

/** Invite token kept across the sign-up/log-in detour so the join flow can resume. */
export const pendingInvite = {
  get: () => sessionStorage.getItem("pendingInvite"),
  set: (token: string) => sessionStorage.setItem("pendingInvite", token),
  clear: () => sessionStorage.removeItem("pendingInvite"),
};
